/**
 * PlaidLinkButton Component
 * Fetches a Plaid Link token on click, then opens the Plaid Link modal.
 * Does NOT create a token on mount — only when the user initiates linking.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { createLinkToken, exchangePublicToken } from '../api/linked-bank-accounts.api';

interface PlaidLinkButtonProps {
  onSuccess: () => void;
}

export function PlaidLinkButton({ onSuccess }: PlaidLinkButtonProps): JSX.Element {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shouldOpenRef = useRef(false);

  const { open, ready } = usePlaidLink({
    token: linkToken ?? '',
    onSuccess: async (publicToken, metadata) => {
      const firstAccount = metadata.accounts[0];
      if (!firstAccount) return;
      try {
        await exchangePublicToken({
          publicToken,
          institutionId: metadata.institution?.institution_id ?? '',
          accountId: firstAccount.id,
          accountName: firstAccount.name,
          accountMask: firstAccount.mask ?? '',
          accountType: firstAccount.subtype as 'checking' | 'savings',
        });
        onSuccess();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to link account');
      } finally {
        setIsLoading(false);
      }
    },
    onExit: () => {
      setIsLoading(false);
    },
  });

  // Auto-open Plaid Link once the token is fetched and the handler is ready
  useEffect(() => {
    if (shouldOpenRef.current && ready && linkToken) {
      shouldOpenRef.current = false;
      open();
    }
  }, [ready, linkToken, open]);

  const handleClick = useCallback(async () => {
    if (linkToken && ready) {
      open();
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const { linkToken: token } = await createLinkToken();
      shouldOpenRef.current = true;
      setLinkToken(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to initialize bank linking');
      setIsLoading(false);
    }
  }, [linkToken, ready, open]);

  if (error) {
    return (
      <div className="flex items-center gap-2">
        <p className="text-sm text-destructive m-0">{error}</p>
        <button
          type="button"
          onClick={handleClick}
          className="px-3 py-1.5 text-sm font-medium text-destructive border border-destructive/20 rounded-md hover:bg-destructive/5 min-h-[44px]"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid="plaid-link-button"
      onClick={handleClick}
      disabled={isLoading}
      className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 min-h-[44px]"
    >
      {isLoading ? 'Connecting...' : 'Link Bank Account'}
    </button>
  );
}
